/**
 * Logs or edits an activity (PRD §5.8).
 *
 * A native `<select>` for the type and a native date input: both work
 * identically with a keyboard, a screen reader and a touch screen, and neither
 * needs a custom widget's worth of behaviour to be reimplemented.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.8
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Input } from '../../ui/Input';

import {
  ACTIVITY_TYPES,
  activityFormSchema,
  todayForInput,
  type ActivityFormInput,
  type ActivityFormOutput,
} from './activity-form-schema';

export interface ActivityFormProps {
  /** Prefilled when editing an existing entry. */
  defaultValues?: ActivityFormInput;
  onSubmit: (values: ActivityFormOutput) => Promise<void>;
  /** Required: a form with no way out is a trap. */
  onCancel: () => void;
  submitLabel: string;
  submittingLabel: string;
  /** Shown when the submission fails. */
  errorMessage: string;
}

export function ActivityForm({
  defaultValues,
  onSubmit,
  onCancel,
  submitLabel,
  submittingLabel,
  errorMessage,
}: ActivityFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ActivityFormInput, unknown, ActivityFormOutput>({
    resolver: zodResolver(activityFormSchema),
    defaultValues: defaultValues ?? {
      type: 'CALL',
      /* Most entries are logged the day they happen. */
      occurredOn: todayForInput(),
      summary: '',
    },
  });

  const submit = handleSubmit(async (values) => {
    setSubmitError(null);

    try {
      /* Sent as-is: `zodResolver` has already applied the schema's transforms,
         so re-parsing would feed transformed output back into the input
         schema. */
      await onSubmit(values);
    } catch {
      setSubmitError(errorMessage);
    }
  });

  return (
    /* `noValidate` so our own messages are shown rather than the browser's,
       which cannot be translated or announced consistently. */
    <form
      onSubmit={(event) => {
        void submit(event);
      }}
      noValidate
      className="flex flex-col gap-3"
    >
      {/* No invalid state wired: the type is a closed enum with a default, so
          the field cannot be empty or out of range — a caller cannot reach the
          invalid path, and pretending otherwise would leave dead branches.

          `invalid` and `errorId` are still destructured out. Left in `rest`
          they reach the native `<select>`, which React then warns about as
          unrecognised DOM attributes — the warning names `errorId` and is
          emitted on every render of this form. */}
      <Field label={t('activity.type')} required>
        {({ id, required, invalid: _invalid, errorId: _errorId, ...rest }) => (
          <select
            id={id}
            required={required}
            {...rest}
            className="border-gray-200 text-navy-text min-h-11 w-full rounded-md border bg-white px-3 text-sm"
            {...register('type')}
          >
            {ACTIVITY_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`activity.types.${type}`)}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field label={t('activity.occurredAt')} error={errors.occurredOn?.message} required>
        {({ id, invalid, errorId, required, ...rest }) => (
          <Input
            id={id}
            type="date"
            required={required}
            invalid={invalid}
            errorId={errorId}
            {...rest}
            {...register('occurredOn')}
          />
        )}
      </Field>

      <Field label={t('activity.summary')} error={errors.summary?.message} required>
        {({ id, invalid, errorId, required, ...rest }) => (
          <Input
            id={id}
            required={required}
            invalid={invalid}
            errorId={errorId}
            placeholder={t('activity.summaryPlaceholder')}
            {...rest}
            {...register('summary')}
          />
        )}
      </Field>

      {submitError === null ? null : (
        <p role="alert" className="text-sem-bad text-sm">
          {submitError}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? submittingLabel : submitLabel}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
      </div>
    </form>
  );
}
